import type {
    CreditNoteSorting,
    DocumentDto,
    GetGemasOrderResponse,
    GetOrderResponse,
    OrderPositionDto,
    PartnerDto,
} from './generated';

export const AUTOMATIC_BILLING_LIMIT = 500; // The limit for automatic billing in €

// Handled by the Pipeline!
export const FE_VERSION = '2.1.9-PullRequest88.2';
export const HALF_SECOND_IN_MS = 500;
export const ONE_SECOND_IN_MS = 2 * HALF_SECOND_IN_MS;
export const TWO_SECONDS_IN_MS = 2 * ONE_SECOND_IN_MS;
export const ONE_MINUTE_IN_MS = 60 * ONE_SECOND_IN_MS;
export const TEN_MINUTES_IN_MS = 10 * ONE_MINUTE_IN_MS;
export const FIVE_MINUTES_IN_SECONDS = 5 * 60;
export const AUTH_IFRAME_HASH_TIMEOUT = 5_000;
export const TOAST_LIFETIME = 5_000;
export const REVOKE_OBJECT_URL_TIMEOUT = 2_000;
export const ACTION_COLUMN_NAME = 'action-column';
export const POSITION_TABLE_COLUMNS: Partial<keyof OrderPositionDto>[] = [
    'description',
    'quantity',
    'unitPrice',
    'unityOfMeasurement',
    'totalPrice',
];
export const DOCUMENT_TABLE_COLUMNS: Partial<keyof DocumentDto>[] = ['description'];
export const ORDER_TABLE_COLUMNS: Partial<
    keyof GetOrderResponse | keyof GetGemasOrderResponse | typeof ACTION_COLUMN_NAME
>[] = ['id', 'damage', 'isConstruction', 'changedAt', ACTION_COLUMN_NAME];
export const PROCESSED_TABLE_COLUMNS: Partial<
    | keyof GetOrderResponse
    | keyof GetGemasOrderResponse
    | keyof PartnerDto
    | typeof ACTION_COLUMN_NAME
>[] = [
    'name',
    'partner',
    'id',
    'inventory',
    'damage',
    'isConstruction',
    'changedAt',
    ACTION_COLUMN_NAME,
];
export const CREDIT_NOTE_TABLE_COLUMNS: Partial<keyof typeof CreditNoteSorting>[] = [
    'ClientName',
    'PartnerName',
    'CreationDate',
    'CreditNoteNumber',
];
export const SPECIAL_TABLE_COLUMNS = ['name', ACTION_COLUMN_NAME] as (
    keyof PartnerDto | typeof ACTION_COLUMN_NAME
)[];

export const UNKNOWN_SETTLEMENT_KIND = 'unknown';

export const MAX_KEY_VALUES_LENGTH = 3;
export const MIN_AUTOCOMPLETE_DIGITS = 3;
export const DEFAULT_PAGE_SIZE = 6;
export const DEFAULT_DEBOUNCE_TIME = 300;

export const QUERY_ORDER = 'single-order';
export const QUERY_ORDERS = 'order-array';
export const QUERY_DOCUMENT = 'single-document';
export const QUERY_PROCESSED_ORDERS = 'processed-order-array';
export const QUERY_SPECIAL_CASE_LIST = 'special-case-list';
export const QUERY_SPECIAL_CASE_AUTOCOMPLETE = 'special-case-autocomplete';
export const QUERY_SPECIAL_CASE_PARTNERS = 'special-case-partners-array';
export const QUERY_E2E_RESET = 'reset-data';
export const QUERY_TRANSFER = 'transfer';
export const QUERY_CLIENTS = 'clients';
export const QUERY_SET_ORDER_CLIENT = 'set-order-client';
export const QUERY_USERS = 'users';
export const QUERY_LOCATIONS = 'locations';
export const QUERY_LOCATION_INFO = 'location-info';
export const QUERY_CREDIT_NOTE_LIST = 'credit-note-list';

export const QUERY_SET_ORDER_IS_CONSTRUCTION_SERVICE = 'order-set-is-construction-service';

// Informational responses (100–199)
export const HTTP_STATUS_CODE_CONTINUE = 100; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/100
export const HTTP_STATUS_CODE_SWITCHING_PROTOCOLS = 101; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/101
export const HTTP_STATUS_CODE_PROCESSING = 102; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/102
export const HTTP_STATUS_CODE_EARLY_HINTS = 103; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/103

// Successful responses (200–299)
export const HTTP_STATUS_CODE_OK = 200; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/200
export const HTTP_STATUS_CODE_CREATED = 201; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/201
export const HTTP_STATUS_CODE_ACCEPTED = 202; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/202
export const HTTP_STATUS_CODE_NON_AUTHORITATIVE_INFORMATION = 203; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/203
export const HTTP_STATUS_CODE_NO_CONTENT = 204; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/204
export const HTTP_STATUS_CODE_RESET_CONTENT = 205; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/205
export const HTTP_STATUS_CODE_PARTIAL_CONTENT = 206; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/206
export const HTTP_STATUS_CODE_MULTI_STATUS = 207; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/207
export const HTTP_STATUS_CODE_ALREADY_REPORTED = 208; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/208
export const HTTP_STATUS_CODE_IM_USED = 226; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/226

// Redirection messages (300–399)
export const HTTP_STATUS_CODE_MULTIPLE_CHOICES = 300; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/300
export const HTTP_STATUS_CODE_MOVED_PERMANENTLY = 301; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/301
export const HTTP_STATUS_CODE_FOUND = 302; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/302
export const HTTP_STATUS_CODE_SEE_OTHER = 303; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/303
export const HTTP_STATUS_CODE_NOT_MODIFIED = 304; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/304
export const HTTP_STATUS_CODE_USE_PROXY = 305; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/305
export const HTTP_STATUS_CODE_SWITCH_PROXY = 306; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/306
export const HTTP_STATUS_CODE_TEMPORARY_REDIRECT = 307; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/307
export const HTTP_STATUS_CODE_PERMANENT_REDIRECT = 308; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/308

// Client error responses (400–499)
export const HTTP_STATUS_CODE_BAD_REQUEST = 400; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/400
export const HTTP_STATUS_CODE_UNAUTHORIZED = 401; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/401
export const HTTP_STATUS_CODE_PAYMENT_REQUIRED = 402; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/402
export const HTTP_STATUS_CODE_FORBIDDEN = 403; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/403
export const HTTP_STATUS_CODE_NOT_FOUND = 404; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/404
export const HTTP_STATUS_CODE_METHOD_NOT_ALLOWED = 405; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/405
export const HTTP_STATUS_CODE_NOT_ACCEPTABLE = 406; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/406
export const HTTP_STATUS_CODE_PROXY_AUTHENTICATION_REQUIRED = 407; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/407
export const HTTP_STATUS_CODE_REQUEST_TIMEOUT = 408; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/408
export const HTTP_STATUS_CODE_CONFLICT = 409; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/409
export const HTTP_STATUS_CODE_GONE = 410; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/410
export const HTTP_STATUS_CODE_LENGTH_REQUIRED = 411; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/411
export const HTTP_STATUS_CODE_PRECONDITION_FAILED = 412; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/412
export const HTTP_STATUS_CODE_PAYLOAD_TOO_LARGE = 413; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/413
export const HTTP_STATUS_CODE_URI_TOO_LONG = 414; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/414
export const HTTP_STATUS_CODE_UNSUPPORTED_MEDIA_TYPE = 415; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/415
export const HTTP_STATUS_CODE_RANGE_NOT_SATISFIABLE = 416; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/416
export const HTTP_STATUS_CODE_EXPECTATION_FAILED = 417; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/417
export const HTTP_STATUS_CODE_IM_A_TEAPOT = 418; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/418
export const HTTP_STATUS_CODE_MISDIRECTED_REQUEST = 421; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/421
export const HTTP_STATUS_CODE_UNPROCESSABLE_ENTITY = 422; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/422
export const HTTP_STATUS_CODE_LOCKED = 423; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/423
export const HTTP_STATUS_CODE_FAILED_DEPENDENCY = 424; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/424
export const HTTP_STATUS_CODE_TOO_EARLY = 425; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/425
export const HTTP_STATUS_CODE_UPGRADE_REQUIRED = 426; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/426
export const HTTP_STATUS_CODE_PRECONDITION_REQUIRED = 428; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/428
export const HTTP_STATUS_CODE_TOO_MANY_REQUESTS = 429; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/429
export const HTTP_STATUS_CODE_REQUEST_HEADER_FIELDS_TOO_LARGE = 431; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/431
export const HTTP_STATUS_CODE_UNAVAILABLE_FOR_LEGAL_REASONS = 451; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/451

// Server error responses (500–599)
export const HTTP_STATUS_CODE_INTERNAL_SERVER_ERROR = 500; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/500
export const HTTP_STATUS_CODE_NOT_IMPLEMENTED = 501; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/501
export const HTTP_STATUS_CODE_BAD_GATEWAY = 502; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/502
export const HTTP_STATUS_CODE_SERVICE_UNAVAILABLE = 503; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/503
export const HTTP_STATUS_CODE_GATEWAY_TIMEOUT = 504; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/504
export const HTTP_STATUS_CODE_HTTP_VERSION_NOT_SUPPORTED = 505; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/505
export const HTTP_STATUS_CODE_VARIANT_ALSO_NEGOTIATES = 506; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/506
export const HTTP_STATUS_CODE_INSUFFICIENT_STORAGE = 507; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/507
export const HTTP_STATUS_CODE_LOOP_DETECTED = 508; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/508
export const HTTP_STATUS_CODE_NOT_EXTENDED = 510; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/510
export const HTTP_STATUS_CODE_NETWORK_AUTHENTICATION_REQUIRED = 511; // https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/511
